import { Typography, Box, Card, Checkbox, FormControlLabel, useTheme } from '@mui/material'
import React, { FC, useState, useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Trans } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehype from 'rehype-raw'
import { LoadingButton } from '@mui/lab'
import { J2Document } from '@e3dc-react/icons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import LinkVariant from 'src/Elements/Buttons/LinkVariant'
import { supportPhoneNumbers } from 'src/Libs/contact/configurations'
import { getReactMarkdownComponents } from 'src/Libs/notifications/config'
import { UserData } from 'src/State/User/UserModel'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { CompanyContactData } from 'src/Libs/user/models'
import { getCityWithZip } from 'src/Libs/utils/string-functions'
import useNdaSubmitting from 'src/Libs/nda/api/hooks/use-nda-submitting'
import { getThemedLogoWithProperties } from 'src/Libs/utils'

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    width: '100%',
    overflow: 'auto',
    boxSizing: 'border-box',
    paddingInline: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: '100svh',
    background: `${theme.palette.common.white}`,
    position: 'fixed',
    zIndex: 1201,
    left: 0,
    top: 0,
  },
  phoneContainer: {
    textAlign: 'center',
    marginBlock: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      position: 'initial',
      marginBlock: `${theme.spacing(3)}`,
    },
  },
  logo: {
    position: 'absolute',
    top: theme.spacing(10),
    left: theme.spacing(10),
    width: '132px',
    [theme.breakpoints.down('md')]: {
      position: 'initial',
      alignSelf: 'flex-start',
      marginTop: theme.spacing(4),
      marginLeft: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      width: '100px',
    },
  },
  documentIcon: {
    width: '76px',
    height: '76px',
    color: theme.palette.primary.main,
  },
  card: {
    width: '100%',
    maxWidth: '570px',
    padding: 0,
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(3),
    },
  },
  cardTextContainer: {
    padding: theme.spacing(4),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: `${theme.spacing(2)}`,
    boxSizing: 'border-box',
  },
  cardContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  agreementMainContainer: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingRight: theme.spacing(0.5),
    background: theme.palette.background.paper2,
    border: `1px solid ${theme.palette.shades.shade03}`,
    borderRadius: theme.shape.borderRadius,
  },
  scrollAgreementContainer: {
    padding: theme.spacing(2),
    overflow: 'auto',
    height: '300px',
    textAlign: 'start',
    boxSizing: 'border-box',
    '::-webkit-scrollbar': {
      width: '4px',
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      background: theme.palette.grey.grey05,
    },
  },
  checkboxAgreementContainer: {
    borderTop: `1px solid ${theme.palette.shades.shade03}`,
    paddingTop: `${theme.spacing(2)}`,
    marginTop: `${theme.spacing(2)}`,
    display: 'flex',
    alignItems: 'center',
  },
  submitContainer: {
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    borderTop: `1px solid ${theme.palette.shades.shade03}`,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  supportLink: {
    color: theme.palette.primary.main,
  },
}))

interface NdaPageProps {
  user: UserData
  companyName: string
  companyLocation?: CompanyContactData
  refetchCompanyData: () => void
}

/** NDA page component */
const NdaPage: FC<NdaPageProps> = ({ user, companyName, refetchCompanyData, companyLocation }) => {
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const theme = useTheme()
  const reactMarkdownComponents = useMemo(() => getReactMarkdownComponents(theme), [theme])
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const [isButtonUnlocked, setIsButtonUnlocked] = useState(false)
  const { logo, backgroundImage, backgroundSize } = getThemedLogoWithProperties()

  const { mutate, isPending: isSubmitLoading } = useNdaSubmitting({
    companyId: user.Firmen_ID as number,
    onSuccess: () => {
      enqueueSuccessStack(t('nda.nda-agreement.submit-success-message'))
      refetchCompanyData()
    },
    onError: () => {
      enqueueErrorStack(t('nda.nda-agreement.submit-error-message'))
    },
  })
  const supportPhoneNumber = supportPhoneNumbers.DEU.business

  const onCheckboxChange = (checked: boolean): void => {
    setIsButtonUnlocked(checked)
  }

  const onSubmitNda = (): void => {
    mutate()
  }

  return (
    <Box className={classes.mainContainer}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `url(${backgroundImage}) no-repeat center`,
          backgroundSize,
          opacity: 0.2,
          zIndex: -1,
        }}
      />
      <Box className={classes.logo} component={'img'} src={logo} />
      <Box className={classes.cardContainer}>
        <Card className={classes.card}>
          <Box className={classes.cardTextContainer}>
            <J2Document className={classes.documentIcon} />
            <Typography variant="h2" component={'h2'}>
              {t('nda.nda-agreement.title')}
            </Typography>
            <Typography variant="body1" component={'h3'}>
              {t('nda.nda-agreement.subtitle')}
            </Typography>
            <Box className={classes.agreementMainContainer}>
              <Box className={classes.scrollAgreementContainer}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehype]} components={reactMarkdownComponents}>
                  {t('nda.nda-agreement.agreement.start', {
                    name: companyName,
                    street: companyLocation?.PlaceOR,
                    city: getCityWithZip(companyLocation?.PLZ, companyLocation?.Address),
                  }) + t('nda.nda-agreement.agreement.middle')}
                </ReactMarkdown>
                <Box className={classes.checkboxAgreementContainer}>
                  <FormControlLabel
                    control={<Checkbox name={'accepted'} />}
                    label={<Typography variant="body1">{t('nda.nda-agreement.checkbox-text', { companyName: companyName })}</Typography>}
                    checked={isButtonUnlocked}
                    onChange={(_, checked) => onCheckboxChange(checked)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box className={classes.submitContainer}>
            <LoadingButton variant="contained" disabled={!isButtonUnlocked || isSubmitLoading} onClick={onSubmitNda} loading={isSubmitLoading}>
              {t('nda.nda-agreement.submit-button-text')}
            </LoadingButton>
          </Box>
        </Card>
      </Box>
      <Typography variant="h5" className={classes.phoneContainer}>
        <Trans
          i18nKey={'nda.nda-agreement.phone-support-text'}
          components={{
            phoneLink: (
              <LinkVariant
                type="phone"
                link={supportPhoneNumber}
                label={supportPhoneNumber}
                hasUnderline={false}
                textClassName={classes.supportLink}
                textVariant="body2"
              />
            ),
          }}
        />
      </Typography>
    </Box>
  )
}

export default NdaPage
